import { stringifyLosslessJson } from './lossless-json-codec';
import {
    ResourceType } from './authorization-contracts';
import {
    Role,
    isInternalRole,
    isOrganizationalRole,
    isAdminRole,
    RoleRank,
} from './identity-contracts';
import type {
    PermissionSourceDesignator,
    PermissionsGrant,
    ResourceDesignator,
    RoleAssignmentPermissionContext,
} from './permission.types';
import { type RoleAssignmentsContainer, getAllRoleAssignments } from './user.types';

export function isInternalRoleAssignment(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (!activeRoleAssignment) {
        return false;
    }
    return isInternalRole(activeRoleAssignment.role);
}
export function isAnyInternalRoleAssignment(
    allRoleAssignments: ReadonlyArray<RoleAssignmentPermissionContext>
): boolean {
    for (const roleAssignment of allRoleAssignments) {
        if (isInternalRoleAssignment(roleAssignment)) {
            return true;
        }
    }
    return false;
}

export function hasAnyInternalRole(
    userProfile: RoleAssignmentsContainer<RoleAssignmentPermissionContext>
): boolean {
    const allRoleAssignments = getAllRoleAssignments(userProfile);
    return isAnyInternalRoleAssignment(allRoleAssignments);
}

export function isOrganizationalRoleAssignment(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (!activeRoleAssignment) {
        return false;
    }
    return isOrganizationalRole(activeRoleAssignment.role);
}
export function isAnyOrganizationalRoleAssignment(
    allRoleAssignments: ReadonlyArray<RoleAssignmentPermissionContext>
): boolean {
    for (const roleAssignment of allRoleAssignments) {
        if (isOrganizationalRoleAssignment(roleAssignment)) {
            return true;
        }
    }
    return false;
}

export function hasAnyAdminRole(
    userProfile: RoleAssignmentsContainer<RoleAssignmentPermissionContext>
): boolean {
    const allRoleAssignments = getAllRoleAssignments(userProfile);
    return isAnyAdminRoleAssignment(allRoleAssignments);
}

export function isAdminRoleAssignment(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (!activeRoleAssignment) {
        return false;
    }
    return isAdminRole(activeRoleAssignment.role);
}
export function isAnyAdminRoleAssignment(
    allRoleAssignments: ReadonlyArray<RoleAssignmentPermissionContext>
): boolean {
    for (const roleAssignment of allRoleAssignments) {
        if (isAdminRoleAssignment(roleAssignment)) {
            return true;
        }
    }
    return false;
}

export function hasAnyOrganizationalRole(
    userProfile: RoleAssignmentsContainer<RoleAssignmentPermissionContext>
): boolean {
    const allRoleAssignments = getAllRoleAssignments(userProfile);
    return isAnyOrganizationalRoleAssignment(allRoleAssignments);
}

export function hasIsoOrganizationCapacity(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (activeRoleAssignment === null) {
        return false;
    }
    if (!isOrganizationalRole(activeRoleAssignment.role)) {
        return false;
    }
    return activeRoleAssignment.organization_is_iso;
}

export function hasMerchantOrganizationCapacity(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (activeRoleAssignment === null) {
        return false;
    }
    if (!isOrganizationalRole(activeRoleAssignment.role)) {
        return false;
    }
    return activeRoleAssignment.organization_is_merchant;
}

export function hasCustomerOrganizationCapacity(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (activeRoleAssignment === null) {
        return false;
    }
    if (!isOrganizationalRole(activeRoleAssignment.role)) {
        return false;
    }
    return activeRoleAssignment.organization_is_customer;
}

export function hasOrganizationalAdminPrivileges(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (!activeRoleAssignment) {
        return false;
    }
    if (!isOrganizationalRole(activeRoleAssignment.role)) {
        return false;
    }
    const roleRank = RoleRank[activeRoleAssignment.role];
    const adminRank = RoleRank[Role.ADMIN];
    return roleRank !== undefined && roleRank >= adminRank;
}

export function hasSuperadminPrivileges(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    return activeRoleAssignment?.role === Role.SUPERADMIN;
}

export function hasInternalAdminPrivileges(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (!activeRoleAssignment) {
        return false;
    }
    const roleRank = RoleRank[activeRoleAssignment.role];
    const pillarAdminRank = RoleRank[Role.INTERNAL_ADMIN];
    return roleRank !== undefined && roleRank >= pillarAdminRank;
}

// This function is wrong: to determine if an organizational user has privileges over
// another it is necessary to check the hierarchical relationship between their organizations.
// export function hasOrganizationalAdminPrivilegesForOrganization(
//     organization_id: number,
//     activeRoleAssignment: RoleAssignmentDetailed | null
// ): boolean {
//     return (
//         hasInternalAdminPrivileges(user, activeRoleAssignment) ||
//         hasOrganizationalAdminPrivileges(activeRoleAssignment)
//     );
// }

// This function is wrong: to determine if an organizational user has privileges over
// another it is necessary to check the hierarchical relationship between their organizations.
// export function hasOrganizationalAdminPrivilegesForTarget(
//     user: UserProfileResponse | null,
//     organization_id: number,
//     activeRoleAssignment: RoleAssignmentDetailed | null
// ): boolean {
//     return hasAnyOrganizationalRole(
//         user,
//         organization_id,
//         [Role.PRINCIPAL, Role.ADMIN],
//         activeRoleAssignment
//     );
// }

// This function is wrong: to determine if an organizational user has privileges over
// another it is necessary to check the hierarchical relatioship between their organizations.
// export function hasMerchantRepPrivileges(
//     organizationId: number,
//     activeRoleAssignment: RoleAssignmentDetailed | null
// ): boolean {
//     if (activeRoleAssignment === null) {
//         return false;
//     }
//     return AllMerchantRoles.includes(activeRoleAssignment.role);
// }

// canDoXyz: gating functions (only these should be used for authorization)

export function hasInternalUsersPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    return hasInternalAdminPrivileges(activeRoleAssignment);
}

export function resourceDesignatorKey(
    designator: PermissionSourceDesignator | ResourceDesignator
): string {
    if (
        designator.designator_type === 'WILDCARD'
        || designator.designator_type === 'SELF'
    ) {
        return `designator:${designator.designator_type}`;
    }
    switch (designator.designator_type) {
        case ResourceType.ORGANIZATION:
            return `${designator.designator_type}:${designator.organization_uuid}:${designator.descendants}`;
        case ResourceType.USER:
            return `${designator.designator_type}:${designator.user_uuid}`;
        case ResourceType.ROLE_ASSIGNMENT:
            return `${designator.designator_type}:${designator.role_assignment_uuid}`;
        case ResourceType.CREDENTIAL:
            return `${designator.designator_type}:${designator.credential_uuid}`;
        case ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY:
            return (
                `${designator.designator_type}:`
                + designator.organization_ipx_credential_policy_uuid
            );
        case ResourceType.IPX_CREDENTIAL_DEFINITION:
            return `${designator.designator_type}:`
                + designator.ipx_credential_definition_uuid;
        case ResourceType.IPX_WEB_RESOURCE_MODEL:
            return `${designator.designator_type}:`
                + designator.ipx_web_resource_model_uuid;
        case ResourceType.ALLOWED_WEBSITE_TEMPLATE:
            return `${designator.designator_type}:`
                + designator.allowed_website_template_uuid;
        case ResourceType.IPW:
            return `${designator.designator_type}:${designator.ipw_system_id}`;
        case ResourceType.IPG:
            return `${designator.designator_type}:${designator.ipg_system_id}`;
        case ResourceType.PROCESSOR:
            return `${designator.designator_type}:${designator.processor_uuid}`;
        case ResourceType.MERCHANT_ACCOUNT:
            return `${designator.designator_type}:${designator.merchant_account_uuid}`;
        case ResourceType.LOGICAL_TERMINAL:
            return `${designator.designator_type}:${designator.logical_terminal_uuid}`;
        case ResourceType.TERMINAL_DEVICE:
            return `${designator.designator_type}:${designator.terminal_device_uuid}`;
        case ResourceType.PAYMENT_TRANSACTION:
            return `${designator.designator_type}:${designator.payment_transaction_uuid}`;
        case ResourceType.PAYMENT_REQUEST:
            return `${designator.designator_type}:${designator.payment_request_uuid}`;
        case ResourceType.PAYMENT_METHOD_TOKEN:
            return `${designator.designator_type}:${designator.payment_method_token_uuid}`;
        case ResourceType.PERMISSIONS_BUNDLE:
            return `${designator.designator_type}:${designator.permissions_bundle_uuid}`;
        case ResourceType.PERMISSION_POLICY:
            return `${designator.designator_type}:${designator.permission_policy_uuid}`;
        case ResourceType.RPA_MODULE:
            return `${designator.designator_type}:${designator.rpa_module_name}`;
    }
}

export function formatResourceDesignator(
    designator: PermissionSourceDesignator | ResourceDesignator
): string {
    if (
        designator.designator_type === 'WILDCARD'
        || designator.designator_type === 'SELF'
    ) {
        return designator.designator_type.toLowerCase();
    }
    switch (designator.designator_type) {
        case ResourceType.ORGANIZATION:
            return designator.descendants
                ? `organization:${designator.organization_uuid}:descendants`
                : `organization:${designator.organization_uuid}`;
        case ResourceType.USER:
            return `user:${designator.user_uuid}`;
        case ResourceType.ROLE_ASSIGNMENT:
            return `role_assignment:${designator.role_assignment_uuid}`;
        case ResourceType.CREDENTIAL:
            return `credential:${designator.credential_uuid}`;
        case ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY:
            return (
                'organization_ipx_credential_policy:'
                + designator.organization_ipx_credential_policy_uuid
            );
        case ResourceType.IPX_CREDENTIAL_DEFINITION:
            return 'ipx_credential_definition:'
                + designator.ipx_credential_definition_uuid;
        case ResourceType.IPX_WEB_RESOURCE_MODEL:
            return 'ipx_web_resource_model:'
                + designator.ipx_web_resource_model_uuid;
        case ResourceType.ALLOWED_WEBSITE_TEMPLATE:
            return 'allowed_website_template:'
                + designator.allowed_website_template_uuid;
        case ResourceType.IPW:
            return `ipw:${designator.ipw_system_id}`;
        case ResourceType.IPG:
            return `ipg:${designator.ipg_system_id}`;
        case ResourceType.PROCESSOR:
            return `processor:${designator.processor_uuid}`;
        case ResourceType.MERCHANT_ACCOUNT:
            return `merchant_account:${designator.merchant_account_uuid}`;
        case ResourceType.LOGICAL_TERMINAL:
            return `logical_terminal:${designator.logical_terminal_uuid}`;
        case ResourceType.TERMINAL_DEVICE:
            return `terminal_device:${designator.terminal_device_uuid}`;
        case ResourceType.PAYMENT_TRANSACTION:
            return `payment_transaction:${designator.payment_transaction_uuid}`;
        case ResourceType.PAYMENT_REQUEST:
            return `payment_request:${designator.payment_request_uuid}`;
        case ResourceType.PAYMENT_METHOD_TOKEN:
            return `payment_method_token:${designator.payment_method_token_uuid}`;
        case ResourceType.PERMISSIONS_BUNDLE:
            return `permissions_bundle:${designator.permissions_bundle_uuid}`;
        case ResourceType.PERMISSION_POLICY:
            return `permission_policy:${designator.permission_policy_uuid}`;
        case ResourceType.RPA_MODULE:
            return `rpa_module:${designator.rpa_module_name}`;
    }
}

export function permissionsGrantKey(permission: PermissionsGrant): string {
    const normalizedActions = [...permission.actions].sort();
    const normalizedResources = [...permission.resources]
        .map(resourceDesignatorKey)
        .sort();
    return stringifyLosslessJson({
        effect: permission.effect,
        resource_type: permission.resource_type,
        actions: normalizedActions,
        resources: normalizedResources,
    });
}

export function hasIsoUsersPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (hasInternalAdminPrivileges(activeRoleAssignment)) {
        return true;
    }
    return hasOrganizationalAdminPrivileges(activeRoleAssignment);
}

export function hasMerchantUsersPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (hasInternalAdminPrivileges(activeRoleAssignment)) {
        return true;
    }
    return hasOrganizationalAdminPrivileges(activeRoleAssignment);
}

export function hasOrganizationalUsersPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (hasInternalAdminPrivileges(activeRoleAssignment)) {
        return true;
    }
    return hasOrganizationalAdminPrivileges(activeRoleAssignment);
}

export function hasCustomerUsersPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    // For menu visibility (no target context), only check internal roles
    // Organization-specific checks happen at the page/operation level
    return hasInternalAdminPrivileges(activeRoleAssignment);
}

export function hasOrganizationsPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (hasInternalAdminPrivileges(activeRoleAssignment)) {
        return true;
    }
    return hasOrganizationalAdminPrivileges(activeRoleAssignment);
}

export function hasIsoOrganizationsPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    return hasOrganizationsPage(activeRoleAssignment);
}

export function hasMerchantOrganizationsPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    return hasOrganizationsPage(activeRoleAssignment);
}

export function hasPaymentsInfrastructurePage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    return hasOrganizationsPage(activeRoleAssignment);
}

export function hasAllowedWebsiteTemplatesPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    return hasInternalAdminPrivileges(activeRoleAssignment);
}

export function hasCustomersPage(
    activeRoleAssignment: RoleAssignmentPermissionContext | null
): boolean {
    if (hasInternalAdminPrivileges(activeRoleAssignment)) {
        return true;
    }
    return (
        hasIsoOrganizationCapacity(activeRoleAssignment)
        || hasMerchantOrganizationCapacity(activeRoleAssignment)
    );
}

// These two functions are wrong: they need access to organizational hierarchy
// export function canAccessSambaConfig(
//     organization_id: number,
//     activeRoleAssignment: RoleAssignmentDetailed | null
// ): boolean {
//     return (
//         hasInternalAdminPrivileges(activeRoleAssignment) ||
//         hasIsoAdminPrivileges(organization_id, activeRoleAssignment)
//     );
// }

// export function canCreateUserForOrganization(
//     organization_id: number | null,
//     activeRoleAssignment: RoleAssignmentDetailed | null
// ): boolean {
//     if (!activeRoleAssignment || !organization_id) return false;
//     // INTERNAL_ADMIN/SUPERADMIN can create users in any organization
//     // PRINCIPAL/ADMIN can create users in their organization
//     return (
//         hasInternalAdminPrivileges(activeRoleAssignment) ||
//         hasOrganizationalAdminPrivileges(activeRoleAssignment)
//     );
// }
